import CollectorCard from '../passport/CollectorCard';
import CollectorCardSkeleton from '../passport/CollectorCardSkeleton';
import { sourceCard } from '../passport/sourceCards';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { eventPublicPath } from '../../utils/eventSlug';
import type { BiltEvent } from '../../types/event';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import PageShell from '../ui/PageShell';
import { DirectoryBrowseLayout, FilterSection } from '../ui/DirectoryBrowseLayout';
import { CalendarIcon } from '@heroicons/react/24/outline';
import FeatureTip from '../onboarding/FeatureTip';

function entryCtaLabel(event: BiltEvent): string {
  if (!event.viewer_is_registered) return 'Register your vehicle';
  const status = String(event.viewer_registration_status || '');
  if (status === 'checked_in') return "You're checked in";
  if (status === 'confirmed') return "You're registered";
  if (status === 'payment_pending' || status === 'payment_failed') return 'Complete payment';
  if (status === 'pending_approval' || status === 'submitted' || status === 'approved') {
    return 'View your entry';
  }
  return "You're entered";
}

function entryBadgeLabel(event: BiltEvent): string | null {
  if (!event.viewer_is_registered) return null;
  const status = String(event.viewer_registration_status || '');
  if (status === 'checked_in') return 'Checked in';
  if (status === 'confirmed') return 'Registered';
  if (status === 'payment_pending') return 'Payment needed';
  if (status === 'pending_approval' || status === 'submitted') return 'Entry pending';
  if (status === 'approved') return 'Approved';
  return 'Entered';
}

function formatEventDate(startAt: string | null): string {
  if (!startAt) return 'Date TBD';
  const d = new Date(startAt.replace(' ', 'T'));
  if (isNaN(d.getTime())) return 'Date TBD';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

type EventWhen = 'upcoming' | 'past' | 'all';

type EventFilters = {
  q?: string;
  city?: string;
  state?: string;
  when: EventWhen;
  page: number;
};

function filtersFromParams(params: URLSearchParams): EventFilters {
  const when = (params.get('when') as EventWhen) || 'upcoming';
  return {
    q: params.get('q') || undefined,
    city: params.get('city') || undefined,
    state: params.get('state') || undefined,
    when: ['upcoming', 'past', 'all'].includes(when) ? when : 'upcoming',
    page: Math.max(1, Number(params.get('page') || 1)),
  };
}

function filtersToParams(filters: EventFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (filters.q) next.set('q', filters.q);
  if (filters.city) next.set('city', filters.city);
  if (filters.state) next.set('state', filters.state);
  if (filters.when && filters.when !== 'upcoming') next.set('when', filters.when);
  if (filters.page > 1) next.set('page', String(filters.page));
  return next;
}

const EventCardSkeleton: React.FC = () => <CollectorCardSkeleton kind="event" />;

const EventsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [draft, setDraft] = useState<EventFilters>(filters);
  const [searchInput, setSearchInput] = useState(filters.q || '');
  const [events, setEvents] = useState<BiltEvent[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_count: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.q || '');
  }, [filters]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getPublicEvents({
        q: filters.q,
        city: filters.city,
        state: filters.state,
        when: filters.when,
        page: filters.page,
        limit: 20,
      });
      setEvents(result.data);
      setPagination(result.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyDraft = () => {
    setParams(filtersToParams({ ...draft, q: searchInput.trim() || undefined, page: 1 }), {
      replace: true,
    });
    setMobileOpen(false);
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
    setDraft({ when: 'upcoming', page: 1 });
  };

  const activeCount = [
    filters.city,
    filters.state,
    filters.when !== 'upcoming' ? filters.when : null,
  ].filter(Boolean).length;

  const hasActive = activeCount > 0 || Boolean(filters.q);

  return (
    <PageShell
      width="full"
      contentClassName="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1 w-full"
    >
      <DirectoryBrowseLayout
        title="Events"
        description="Every event adds another chapter. Bring a build from your garage; the profile stays connected."
        mobileTriggerLabel="Events"
        sections={[
          { id: 'events-results', label: 'Results', description: 'Upcoming and past shows' },
        ]}
        activeFilterCount={activeCount}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        groups={[
          {
            id: 'filters',
            label: 'Filters',
            children: (
              <div className="space-y-4">
                <FilterSection id="when" label="Timing">
                  <div className="flex flex-wrap gap-2">
                    {(
                      [
                        ['upcoming', 'Upcoming'],
                        ['past', 'Past'],
                        ['all', 'All'],
                      ] as const
                    ).map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        className={draft.when === val ? 'filter-chip-active' : 'filter-chip'}
                        onClick={() => setDraft({ ...draft, when: val })}
                        aria-pressed={draft.when === val}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </FilterSection>
                <FilterSection id="location" label="Location">
                  <label className="block text-sm text-gray-300">
                    City
                    <input
                      className="mt-1 w-full input min-h-[40px]"
                      value={draft.city || ''}
                      onChange={(e) => setDraft({ ...draft, city: e.target.value || undefined })}
                      autoComplete="address-level2"
                    />
                  </label>
                  <label className="block text-sm text-gray-300">
                    State / region
                    <input
                      className="mt-1 w-full input min-h-[40px]"
                      value={draft.state || ''}
                      onChange={(e) => setDraft({ ...draft, state: e.target.value || undefined })}
                      autoComplete="address-level1"
                    />
                  </label>
                </FilterSection>
              </div>
            ),
            footer: (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setDraft({ when: 'upcoming', page: 1 })}
                >
                  Reset
                </Button>
                <Button type="button" variant="primary" className="flex-[2]" onClick={applyDraft}>
                  Apply
                </Button>
              </div>
            ),
          },
        ]}
      >
        <div id="events-results">
          <FeatureTip tipId="events_directory" className="mb-4">
            Register a vehicle from your garage so event history and awards stay connected to the
            build.
          </FeatureTip>
          <form
            className="flex flex-col sm:flex-row gap-3 mb-4"
            onSubmit={(e) => {
              e.preventDefault();
              setParams(
                filtersToParams({ ...filters, q: searchInput.trim() || undefined, page: 1 }),
                { replace: true }
              );
            }}
            role="search"
          >
            <label className="sr-only" htmlFor="events-search">
              Search events
            </label>
            <input
              id="events-search"
              className="flex-1 input min-h-[44px]"
              placeholder="Search events, cities, venues…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button type="submit" variant="primary">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2 mb-4" aria-label="Active filters">
            {filters.when !== 'upcoming' ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, when: 'upcoming', page: 1 }), {
                    replace: true,
                  })
                }
              >
                When: {filters.when} ×
              </button>
            ) : null}
            {filters.city ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, city: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                City: {filters.city} ×
              </button>
            ) : null}
            {filters.state ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, state: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                State: {filters.state} ×
              </button>
            ) : null}
            {hasActive ? (
              <button
                type="button"
                className="text-sm text-orange-400 hover:text-orange-300 cursor-pointer"
                onClick={clearAll}
              >
                Clear all
              </button>
            ) : null}
            <span className="text-sm text-gray-500 ml-auto" aria-live="polite">
              {pagination.total_count} events
            </span>
          </div>

          {loading ? (
            <div
              className="directory-collectible-grid"
              role="status"
              aria-live="polite"
            >
              <span className="sr-only">Loading events…</span>
              <EventCardSkeleton />
              <EventCardSkeleton />
              <EventCardSkeleton />
              <EventCardSkeleton />
              <EventCardSkeleton />
              <EventCardSkeleton />
            </div>
          ) : error ? (
            <EmptyState
              icon={<CalendarIcon className="w-12 h-12" />}
              title="Couldn’t load events"
              description={error}
              action={
                <Button variant="primary" onClick={() => void load()}>
                  Try again
                </Button>
              }
            />
          ) : events.length === 0 ? (
            <EmptyState
              imageSrc={hasActive ? undefined : '/images/brand/empty-events-paddock.jpg'}
              imageAlt=""
              title={hasActive ? 'No events match' : 'No events yet'}
              description={
                hasActive
                  ? 'Try clearing a filter or broadening the search.'
                  : 'When organizers publish events, register a build from your garage. No duplicate entry.'
              }
              action={
                hasActive ? (
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="directory-collectible-grid">
                {events.map((event) => (
                  <CollectorCard
                    key={event.id}
                    card={sourceCard({
                      kind: 'event',
                      id: event.id,
                      title: event.title,
                      description: event.description,
                      image: apiService.getFileUrl(event.flyer_path || '') || event.flyer_path,
                      subtitle: [event.city, event.state].filter(Boolean).join(', '),
                      slug: event.slug,
                      facts: {
                        entry_status: entryBadgeLabel(event),
                        start_at: event.start_at,
                        end_at: event.end_at,
                        timezone: event.timezone || null,
                        host: event.organizer_username || null,
                        venue: event.location,
                        city: event.city,
                      },
                    })}
                    href={eventPublicPath({
                      slug: event.slug,
                      organizer_username: event.organizer_username,
                    })}
                    openLabel={entryCtaLabel(event)}
                    fullRecordExtension={
                      <>
                        {entryBadgeLabel(event) && (
                          <p className="text-sm text-orange-200">{entryBadgeLabel(event)}</p>
                        )}
                        <p className="text-sm text-gray-400">{formatEventDate(event.start_at)}</p>
                        {event.description && (
                          <p className="text-sm text-gray-400 line-clamp-2">{event.description}</p>
                        )}
                      </>
                    }
                  />
                ))}
              </div>
              {pagination.total_pages > 1 ? (
                <div className="flex justify-center gap-3 mt-8">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={pagination.page <= 1}
                    onClick={() =>
                      setParams(filtersToParams({ ...filters, page: pagination.page - 1 }), {
                        replace: true,
                      })
                    }
                  >
                    Previous
                  </Button>
                  <span className="text-gray-400 self-center text-sm">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={pagination.page >= pagination.total_pages}
                    onClick={() =>
                      setParams(filtersToParams({ ...filters, page: pagination.page + 1 }), {
                        replace: true,
                      })
                    }
                  >
                    Next
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </DirectoryBrowseLayout>
    </PageShell>
  );
};

export default EventsPage;
